/*******************************************************************************
 * Copyright     : Copyright (c)  2019 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc., and its use or disclosure in whole or in
 *                 part without express written permission of Honeywell Inc.
 *                 is prohibited.
 *
 * Name          : order-summary-actions.ts
 *
 * Project Title : Checkout
 *
 * Description   : Initial version
 *
 * Company       : Honeywell Technology Solutions , Hyderabad
 *
 * @Author       : H352615
 *
 * @Date         : AUG 12, 2019
 *
 * @Version      : Version 1.0
 *
 * By            : H352615
 * On            : Aug 13, 2019
 * Description   : Updated code as ASDS-997
 *
 * By            : Madhu Gali
 * On            : Sep 13, 2019
 * Description   : ASDS-1178
 *
 * By            : H224423
 * On            : Sep 30, 2019
 * Description   : Order Summary Price calculation issue fix.
 *
 * By            : SandeepRaja (E807238)
 * On            : Oct 17, 2019
 * Description   : ASDS-113: Configure and purchase 131-9 APU through PO
 *
 * By            : Madhu Gali - H175591
 * On            : 18 Oct 2019
 * Description   : ASDS-1549
 *
 * By            : Suresh Jaladi(E807958)
 * On            : 21 Oct 2019
 * Description   : ASDS-1550
 *
 * By            : Sandeep Raja
 * On            : 20 Nov 2019
 * Description   : ASDS-1643: Tax Service Exception Scenario
 *
 * By            : E807958
 * On            : 25 Nov 2019
 * Description   : ASDS-1676
 *
 * By            : SandeepRaja
 * On            : NOV 28, 2019
 * Description   : ASDS-1809: Order Summary - Wireless Products
 *
 * By            : H224423
 * On            : Apr 03 2020
 * Description   : ASDS-2190 - API error handling changes
 *
 * By            : H422336
 * On            : Oct 14, 2020
 * Description   : ASDS - 3580 UI Enhancements for Phase - II
 *
 * By            : H422337
 * On            : Oct 27, 2020
 * Description   : ASDS-3552 - UI Enhancements for Phase - II -Migrate to Forge API
 *
 * By            : H422337
 * On            : Sep 05 2022
 * Description   : ASDS-8220 - Dynamically added apu model name
 *
 ******************************************************************************/

import Action from "./Action";
import ActionConstants from "../action-constants";
import ShippingBillingActions from "./shipping-billing-actions";
import { OrderSummaryStats, apuProductWiseTotalPrice } from "../../../../../types";
import OrderDetailsAPI from "../../API/order-details-api";
import { logInfo } from "../../../../utils/console-utility";
import CheckoutConstants from "../../constants/checkout-constants";

class OrderSummaryActions extends Action {
  static getOrderSummary(userId: string) {
    return async (dispatch, getState) => {
      
      dispatch(OrderSummaryActions.getOrderSummaryInitiated());
      const soldToObj = getState().selectAccount?.selectAccountDetails?.payload?.soldTo;
      const shipToObj = getState().selectAccount?.selectAccountDetails?.payload?.shipTo;  

      // Check if soldToObj or shipToObj is undefined before proceeding
      if (!soldToObj?.sapNumber || !shipToObj?.sapNumber) {
        dispatch(OrderSummaryActions.setOrderSummaryError({}, false));
        return; // Exit the function early as necessary data is missing
    }

      const response = await OrderDetailsAPI.getOrderSummary(
        userId,
        soldToObj.sapNumber,
        shipToObj.sapNumber
      );
      
      if ( response.result && response.result.cart ) {
        const orderSummary: OrderSummaryStats = OrderSummaryMapper.mapOrderSummary(
          response
        );
        const taxSuccessOrFailure: boolean = orderSummary.taxSuccessOrFailure === 'S';

        dispatch(OrderSummaryActions.setOrderSummary(orderSummary, taxSuccessOrFailure));
        dispatch(ShippingBillingActions.setPaymentUpdateRequiredStatus(orderSummary.paymentRequired));

      } else {
        dispatch(OrderSummaryActions.setOrderSummaryError({}, false)); // resetting to intial state values
      }
    };
  }

  private static getOrderSummaryInitiated() {
    return {
      type: ActionConstants.GET_ORDER_SUMMARY,
      payload: {
        isFetching: true,
        isSuccess: false
      }
    };
  }

  private static setOrderSummary(data, taxSuccessOrFailure) {
    return {
      type: ActionConstants.SET_ORDER_SUMMARY,
      payload: {
        data,
        taxSuccessOrFailure,
        isFetching: false,
        isSuccess: true
      }
    };
  }
  private static setOrderSummaryError(data, taxSuccessOrFailure) {
    return {
      type: ActionConstants.SET_ORDER_SUMMARY,
      payload: {
        data,
        taxSuccessOrFailure,
        isFetching: false,
        isSuccess: false
      }
    };
  }
}

/**
 * Class for mapping api data to component understandable object.
 */
class OrderSummaryMapper {
  //Maps cart details to OrderSummaryStats.
  static mapOrderSummary(response): OrderSummaryStats {
    let itemCount: number = 0; // to display total number of items in the cart
    let totalPrice: number = 0; // total price of the products in the cart
    let aspireFeaturesCount: number = 0; // total number of Aspire products
    let aspireFeaturesPrice: number = 0; // total price of Aspire products
    let apuContractCount: number = 0; // total number of APU products
    let apuContractPrice: number = 0; // total price of APU products

    let DLMUWProdCount: number = 0; // total number of DLMU-W products
    let DLMUWProdPrice: number = 0; // total price of DLMU-W products

    let analyzerProdCount: number = 0; // total number of ANALYZER SERVICE CENTER products
    let analyzerProdPrice: number = 0; // total price of ANALYZER SERVICE CENTER products

    let faultAnalyzerProdCount: number = 0; // total number of Fault Analyzer Services products
    let faultAnalyzerProdPrice: number = 0; // total price of Fault Analyzer Services products

    let remoteTerminalProdCount: number = 0; // total number of Remote Terminal Basic Services products
    let remoteTerminalProdPrice: number = 0; // total price of Remote Terminal Basic Services products


    let paymentRequired: boolean = true; // whether payment options should be displayed or not
    let purchaseByContractCount: number = 0; // number of products purchase by contracts
    let purchaseByPaymentCount: number = 0; // number of products purchase by payments
    let apuModels: Array<apuProductWiseTotalPrice> = [];
    let productTotalPrice : number = 0;

    if(response.result.cart) {
      response.result.cart.products.forEach(product => {
        // <-------------------START: APU category type handling------------------->
        if (product.categoryName && product.categoryName.toLowerCase() === CheckoutConstants.APU_CATEGORY_NAME) {
          let apuProductConfig = product.customerType.msa ? 'apuMsaSerials' : 'apuNonMsaSerials';
          apuContractCount += product.customerType[apuProductConfig].length;
          apuContractPrice += OrderSummaryMapper.calculateFeaturePrice(
            product.customerType[apuProductConfig]
          );

          // payment should be made only for products which does not have contract number
          if (product.customerType.msa) {
            purchaseByContractCount += product.customerType.apuMsaSerials.filter(apuLineItem => apuLineItem.contractNo).length;
            purchaseByPaymentCount += product.customerType.apuMsaSerials.filter(apuLineItem => !apuLineItem.contractNo).length;
          }

          if(product.customerType[apuProductConfig].length !== 0) {
            if(apuModels?.length !== 0) {
              const indexOfApuModel = apuModels?.findIndex(model => model?.modelName === product.customerType[apuProductConfig][0]?.model);
              if(indexOfApuModel === -1) {
                apuModels.push({modelName: product.customerType[apuProductConfig][0]?.model,totalPrice:OrderSummaryMapper.calculateFeaturePrice(
                  product.customerType[apuProductConfig]
                )});
              }
              else {
                const calculateProductWiseTotalPrice = apuModels[indexOfApuModel].totalPrice + OrderSummaryMapper.calculateFeaturePrice(
                  product.customerType[apuProductConfig]
                );
                apuModels[indexOfApuModel].totalPrice = calculateProductWiseTotalPrice;
              }
            }
            else {
              apuModels.push({modelName: product.customerType[apuProductConfig][0]?.model,totalPrice:OrderSummaryMapper.calculateFeaturePrice(
                product.customerType[apuProductConfig]
              )});
            }
          }
        }
        // <-------------------END: APU category type handling------------------->

        // <-------------------START: Aspire category type handling------------------->
        else if (product.categoryName && product.categoryName.toLowerCase() === CheckoutConstants.ASPIRE_CATEGORY_NAME) {
          aspireFeaturesCount += product.aspire.length;
          aspireFeaturesPrice += OrderSummaryMapper.calculateFeaturePrice(product.aspire);
        }
        // <-------------------END: Aspire category type handling------------------->

        // <-------------------START: Wireless category type handling------------------->
        else if (product.categoryName && product.categoryName.toLowerCase() === CheckoutConstants.DLMUW_CATEGORY_NAME) {
          DLMUWProdCount += product.wireless.length;
          DLMUWProdPrice += OrderSummaryMapper.calculateFeaturePrice(product.wireless);
        }
        else if(product.categoryName && product.categoryName.toLowerCase() === CheckoutConstants.OTHERS_CATEGORY_NAME && product.prodName){
          if(product.prodName.toUpperCase() === 'ANALYZER SERVICE CENTER') {
              analyzerProdCount += product.wireless.length;
              analyzerProdPrice += OrderSummaryMapper.calculateFeaturePrice(product.wireless);
          }
          else if(/Fault Analyzer/i.test(product.prodName)){
            faultAnalyzerProdCount += product.wireless.length;
            faultAnalyzerProdPrice += OrderSummaryMapper.calculateFeaturePrice(product.wireless);
          }
          else if(/Remote Terminal/i.test(product.prodName)){
              remoteTerminalProdCount += product.wireless.length;
              remoteTerminalProdPrice += OrderSummaryMapper.calculateFeaturePrice(product.wireless);
          }
        }
        // <-------------------END: Wireless category type handling------------------->
        else {
          logInfo("Product category (" +product.categoryName +") was not handled in Order summary");
        }
      });
    }

    purchaseByPaymentCount += aspireFeaturesCount + DLMUWProdCount + analyzerProdCount + faultAnalyzerProdCount + remoteTerminalProdCount;
    itemCount += aspireFeaturesCount + apuContractCount + DLMUWProdCount + analyzerProdCount + faultAnalyzerProdCount + remoteTerminalProdCount;
    totalPrice += aspireFeaturesPrice + apuContractPrice + DLMUWProdPrice + analyzerProdPrice + faultAnalyzerProdPrice + remoteTerminalProdPrice;

    // No need to make payment if all the products purchase with contract number(s)
    // Make payment if anyone of the product is without contract or through payment option
    if ((purchaseByContractCount > 0 && purchaseByPaymentCount < 1) || totalPrice === 0) {
      paymentRequired = false;
    }

    const orderSummaryStats: OrderSummaryStats = {
      taxAmount: response.result.tax.tax,
      taxSuccessOrFailure: response.result.tax.successOrFailure,
      itemCount: itemCount,
      totalPrice: totalPrice,
      aspireProductsStats: {
        featuresCount: aspireFeaturesCount,
        featuresPrice: aspireFeaturesPrice
      },
      bmsProductsStats: {},
      apuProductsStats: {
        contractCount: apuContractCount,
        contractPrice: apuContractPrice,
        apuModels: apuModels,
      },
      DLMUWProductsStats: {
        count: DLMUWProdCount,
        price: DLMUWProdPrice
      },
      analyzerSCProductsStats: {
        count: analyzerProdCount,
        price: analyzerProdPrice
      },
      faultAnalyzerSCProductsStats: {
        count: faultAnalyzerProdCount,
        price: faultAnalyzerProdPrice
      },
      remoteTerminalSCProductsStats: {
        count: remoteTerminalProdCount,
        price: remoteTerminalProdPrice
      },
      paymentRequired: paymentRequired
    };

    return orderSummaryStats;
  }

  private static calculateFeaturePrice(features) {
    let featurePrice = 0;
    features.forEach(feature => {
      featurePrice += feature.unitPrice;
    });
    return featurePrice;
  }
}

export default OrderSummaryActions;
